import DiagnosisI from '@interfaces/patients/diagnosisI'
import { difficultyI, recordDifficultyI, WorkPlanI } from '@interfaces/patients/recordI'
import { WorkPlanActivityI } from '@interfaces/patients/workplan-activityI'
import TreatmentI from '@interfaces/patients/treatmentI'
import { VariableI } from '@interfaces/practititoner/appointment/new-appointment'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PrognosisI } from '@interfaces/patients/Prognosis'
import { ReasonI } from '@interfaces/services/services'
import { ActualPregnancyI, PreviousPregnancyI } from '@interfaces/woman/pregnancy'
import { IappliedQuizzSlice } from '@interfaces/quiz/quiz-common'
import { IDisability } from '@interfaces/patients/disabilities'
import { IBasePatient } from '../patient/patient.slice'
import { ICreatedOrder } from '@interfaces/finance/general'

interface PhysicalExamI {
    notes: string
    variables: {
        vitalSing: {
            pulse: VariableI | undefined
            breathingFrec: VariableI | undefined
            temperature: VariableI | undefined
            saturation: VariableI | undefined
        }
        bloodPressure: {
            systolic: VariableI | undefined
            distolic: VariableI | undefined
        }
        bmi: {
            weight: VariableI | undefined
            size: VariableI | undefined
            BMI: string | undefined
        }
    }
}

export interface AppointmentStateI {
    currentTab: string
    patient: IBasePatient,
    startAppointment: boolean,
    isAppointment: boolean,
    appointmentId: string
    recordId: string
    startDate: number
    endDate: number    
    physicalExam: PhysicalExamI
    interrogation: string
    diagnosis: DiagnosisI[]
    diagnosisToUpdate: DiagnosisI[]
    treatments: TreatmentI[]
    treatmentsToUpdate: TreatmentI[]
    workplan: WorkPlanI[]
    workplanActivities: WorkPlanActivityI[]
    workplanActivitiesToUpdate: WorkPlanActivityI[]
    reasons: ReasonI[]
    patientReasons?: ReasonI[]
    reasonAppointment?: ReasonI
    updateVitals?: boolean
    isPhysical?: boolean
    prognosis: PrognosisI[]
    analysisEvaluation: string
    isEDADehydrationRecovery: boolean | null
    orderId?: string | null
    vSOrderId?: string
    captureStudyDevice?: boolean
    workplanOtherId?: string
    updatedPhysicalExam?: boolean
    patientMessage?: string
    customerService?: string,
    temporalRelationshipCode: string,
    difficulty:recordDifficultyI
    currentSuffering: string,
    pregestationalRiskCode: string,
    pregnancyId: string | null,
    womanPregnancyId?: string
    actualPregnancy: ActualPregnancyI | null
    previousPregnancies: PreviousPregnancyI[]
    previousPregnanciesToUpdate: PreviousPregnancyI[]
    appliedQuizzes:IappliedQuizzSlice[]
    disabilities:IDisability[],
    disabilitiesToUpdate: IDisability[]
    difficulties: difficultyI[]
    difficultiesRemoved: string[]
    presentsHealthCard: string | null,
    completeVaccinationSchedule: string | null
    analysisStudyResults?: string | null,
    applicationCancerCard?: string | null,
    telemedicineConsultation?: string | null,
    orderByDevice:ICreatedOrder | null
}

export const newAppointmentSlice = createSlice({
    name: 'new-appointment',
    initialState: {
        currentTab: 'inter',
        patient: {
            name: '',
            username: '',
            patientId:0
        },
        startAppointment: false,
        isAppointment: false,
        appointmentId: '',
        recordId: '',
        physicalExam: {},
        interrogation: '',
        diagnosis: [],
        treatments: [],
        workplan: [],
        workplanActivities: [],
        workplanActivitiesToUpdate: [],
        updateVitals: false,
        isPhysical: false,
        prognosis: [],
        analysisEvaluation: '',
        isEDADehydrationRecovery: null,
        orderId: null,
        vSOrderId: undefined,
        temporalRelationshipCode: '',
        difficulty:{
            someDifficulty:null,
            difficulties:[]
        },
        diagnosisToUpdate: [],
        treatmentsToUpdate: [],
        currentSuffering: '',
        pregestationalRiskCode: '',
        actualPregnancy: null,
        previousPregnancies: [],
        quizAppliances: [],
        disabilities: [],
        disabilitiesToUpdate: [],
        difficulties: [],
        difficultiesRemoved: [],
        analysisStudyResults: null,
        applicationCancerCard: null,
        telemedicineConsultation: null,
        presentsHealthCard:null,
        completeVaccinationSchedule: null,
        orderByDevice: null
    } as unknown as AppointmentStateI,
    reducers: {
        setTab: (state, action: PayloadAction<string>) => {
            state.currentTab = action.payload
        },
        setAppointmentId: (state, action: PayloadAction<{id: string, startDate: number, endDate: number, isPhysical: boolean, orderId: string | null, reasonAppointment: ReasonI, patientMessage?: string}>) => {
            state.appointmentId = action.payload.id
            state.startDate = action.payload.startDate
            state.endDate = action.payload.endDate
            state.isPhysical = action.payload.isPhysical
            state.orderId = action.payload.orderId
            state.reasonAppointment = action.payload.reasonAppointment
            state.patientMessage = action.payload.patientMessage
        },
        startAppointment: (state, action: PayloadAction<{isAppointment: boolean, patient: IBasePatient, startAppointment: boolean}>) => {
            state.isAppointment = action.payload.isAppointment
            state.patient = action.payload.patient
            state.startAppointment = action.payload.startAppointment
        },
        setVsOrderId: (state, action: PayloadAction<{vSOrderId: string}>) => {
            state.vSOrderId = action.payload.vSOrderId
        },
        setAppointment: (state, action: PayloadAction<boolean>) => {
            state.isAppointment = action.payload
        },
        setInterrogation: (state, action: PayloadAction<string>) => {
            state.interrogation = action.payload
        },
        setPhysicalExam: (state, action: PayloadAction<PhysicalExamI>) => {
            state.physicalExam = action.payload
        },
        setPhysicalExamNote: (state, action: PayloadAction<string>) => {
            state.physicalExam.notes = action.payload
        },
        setDiagnosis: (state, action: PayloadAction<DiagnosisI>) => {
            state.diagnosis.push(action.payload)
        },
        setDiagnosisToUpdate: (state, action: PayloadAction<DiagnosisI>) => {
            state.diagnosisToUpdate.push(action.payload)

        },
        setDiagnosisSaved: (state, action: PayloadAction<DiagnosisI[]>) => {
            state.diagnosis = action.payload
        },
        setDiagnosisToUpdateSaved: (state, action: PayloadAction<DiagnosisI[]>) => {
            state.diagnosisToUpdate = action.payload
        },
        deleteDiagnosis: (state, action: PayloadAction<number>) => {
            state.diagnosis.splice(action.payload, 1)
        },
        deleteDiagnosisToUpdate: (state, action: PayloadAction<number>) => {
            state.diagnosisToUpdate.splice(action.payload, 1)
        },
        setTreatment: (state, action: PayloadAction<TreatmentI>) => {
            state.treatments.push(action.payload)
        },
        setTreatmentToUpdate: (state, action: PayloadAction<TreatmentI>) => {
            state.treatmentsToUpdate.push(action.payload)
        },
        setTreatmentSaved: (state, action: PayloadAction<TreatmentI[]>) => {
            state.treatments = action.payload
        },
        setTreatmentToUpdateSaved: (state, action: PayloadAction<TreatmentI[]>) => {
            state.treatmentsToUpdate = action.payload
        },
        setWorkplanActivity: (state, action: PayloadAction<WorkPlanActivityI>) => {
            if(state.workplanActivities){
                state.workplanActivities.push(action.payload)
            }else{
                state.workplanActivities = [action.payload]
            }
        },
        setWorkplanActivityToUpdate: (state, action: PayloadAction<WorkPlanActivityI>) => {
            if(state.workplanActivitiesToUpdate){
                state.workplanActivitiesToUpdate.push(action.payload)
            }else{
                state.workplanActivitiesToUpdate = [action.payload]
            }
        },
        setWorkplanActivitiesSaved: (state, action: PayloadAction<WorkPlanActivityI[]>) => {
            state.workplanActivities = action.payload
        },
        setWorkplanActivitiesToUpdateSaved: (state, action: PayloadAction<WorkPlanActivityI[]>) => {
            state.workplanActivitiesToUpdate = action.payload
        },
        deleteWorkplanActivity: (state, action: PayloadAction<number>) => {
            state.workplanActivities.splice(action.payload, 1)
        },
        deleteWorkplanActivityToUpdate: (state, action: PayloadAction<number>) => {
            state.workplanActivitiesToUpdate.splice(action.payload, 1)
        },
        setRecordId: (state, action: PayloadAction<string>) => {
            state.recordId = action.payload
        },
        setUpdatedPhysicalExam: (state, action: PayloadAction<boolean>) => {
            state.updatedPhysicalExam = action.payload
        },
        setUpdateReasons: (state, action: PayloadAction<ReasonI[]>) => {
            state.reasons = action.payload
        },
        setUpdatePatientReasons: (state, action: PayloadAction<ReasonI[]>) => {
            state.patientReasons = action.payload
        },
        deleteTreatment: (state, action: PayloadAction<number>) => {
            state.treatments.splice(action.payload, 1)
        },
        deleteTreatmentToUpdate: (state, action: PayloadAction<number>) => {
            state.treatmentsToUpdate.splice(action.payload, 1)
        },
        setWorkplan: (state, action: PayloadAction<WorkPlanI[]>) => {
            state.workplan = [...action.payload]
        },
        setWorkPlanOtherId: (state, action: PayloadAction<string>) => {
            state.workplanOtherId = action.payload
        },
        setUpdateVitals: (state, action: PayloadAction<boolean>) => {
            state.updateVitals = action.payload
        },
        setPrognosis: (state, action: PayloadAction<PrognosisI>) => {
            if(state.prognosis){
                state.prognosis.push(action.payload)
            }
            else{
                state.prognosis = []
                state.prognosis.push(action.payload)
            }
        },
        setPrognosisSaved: (state, action: PayloadAction<PrognosisI[]>) => {
            state.prognosis = action.payload
        },
        deletePrognosis: (state, action: PayloadAction<number>) => {
            state.prognosis.splice(action.payload, 1)
        },
        setAnalysisEvaluation: (state, action: PayloadAction<string>) => {
            state.analysisEvaluation = action.payload
        },
        setIsEDADehydrationRecovery: (state, action: PayloadAction<boolean| null>) => {
            state.isEDADehydrationRecovery = action.payload
        },
        setStudyFromDevice: (state, action: PayloadAction<boolean>) => {
            state.captureStudyDevice = action.payload
        },
        setCustomerService: (state, action: PayloadAction<string>) => {
            state.customerService = action.payload
        },
        removeStudyFromDevice: (state) => {
            state.captureStudyDevice = undefined
        },
        setUpdateTemporalRelationship: (state, action: PayloadAction<string>) => {
            state.temporalRelationshipCode = action.payload
        },
        setUpdateDifficulty: (state, action: PayloadAction<recordDifficultyI>) => {
            state.difficulty = action.payload
        },
        setUpdateDifficulties: (state, action: PayloadAction<difficultyI[]>) => {
            state.difficulties = action.payload
        },
        setDifficultiesRemoved: (state, action: PayloadAction<string[]>) => {
            state.difficultiesRemoved = action.payload
        },
        setUpdateCurrentSuffering: (state, action: PayloadAction<string>) => {
            state.currentSuffering = action.payload
        },
        setUpdatePregestationalRiskCode: (state, action: PayloadAction<string>) => {
            state.pregestationalRiskCode = action.payload
        },
        setPregnancyId: (state, action: PayloadAction<string>) => {
            state.pregnancyId = action.payload
        },
        setWomanPregnancyId: (state, action: PayloadAction<string>) => {
            state.womanPregnancyId = action.payload
        },
        setRecordActualPregnancy: (state, action: PayloadAction<ActualPregnancyI>) => {
            state.actualPregnancy = action.payload
        },
        setRecordPreviousPregnancy: (state, action: PayloadAction<PreviousPregnancyI>) => {
            state.previousPregnancies.push(action.payload)
        },
        setRecordPreviousPregnancyToUpdate: (state, action: PayloadAction<PreviousPregnancyI>) => {
            state.previousPregnanciesToUpdate.push(action.payload)
        },
        setRecordPreviousPregnanciesSaved: (state, action: PayloadAction<PreviousPregnancyI[]>) => {
            state.previousPregnancies = action.payload
        },
        setRecordPreviousPregnanciesToUpdateSaved: (state, action: PayloadAction<PreviousPregnancyI[]>) => {
            state.previousPregnanciesToUpdate = action.payload
        },
        setAppliedQuizzAdd: (state, action: PayloadAction<IappliedQuizzSlice>) => {
            if(state.appliedQuizzes){
                state.appliedQuizzes.push(action.payload)
            }else{
                state.appliedQuizzes = [action.payload]
            }
        },
        setAppliedQuizzesSaved: (state, action: PayloadAction<IappliedQuizzSlice[]>) => {
            state.appliedQuizzes = action.payload
        },
        setDisability: (state, action: PayloadAction<IDisability>) => {
            if(state.disabilities){
                state.disabilities.push(action.payload)
            }else{
                state.disabilities = [action.payload]
            }
        },
        setDisabilityToUpdate: (state, action: PayloadAction<IDisability>) => {
            if(state.disabilitiesToUpdate){
                state.disabilitiesToUpdate.push(action.payload)
            }else{
                state.disabilitiesToUpdate = [action.payload]
            }
        },
        setDisabilitiesSaved: (state, action: PayloadAction<IDisability[]>) => {
            state.disabilities = action.payload
        },
        setDisabilitiesToUpdateSaved: (state, action: PayloadAction<IDisability[]>) => {
            state.disabilitiesToUpdate = action.payload
        },
        deleteDisability: (state, action: PayloadAction<number>) => {
            state.disabilities.splice(action.payload, 1)
        },
        deleteDisabilityToUpdate: (state, action: PayloadAction<number>) => {
            state.disabilitiesToUpdate.splice(action.payload, 1)
        },
        setPresentsHealthCard: (state, action: PayloadAction<string | null>) => {
            state.presentsHealthCard = action.payload
        },
        setCompleteVaccinationSchedule: (state, action: PayloadAction<string | null>) => {
            state.completeVaccinationSchedule = action.payload
        },
        setAnalysisStudyResults: (state, action: PayloadAction<string | null>) => {
            state.analysisStudyResults = action.payload
        },
        setAnalysisApplicationCancerCard: (state, action: PayloadAction<string | null>) => {
            state.applicationCancerCard = action.payload
        },
        setTelemedicineConsultation: (state, action: PayloadAction<string | null>) => {
            state.telemedicineConsultation = action.payload
        },
        setOrderByDevice: (state, action: PayloadAction<ICreatedOrder | null>) => {
            state.orderByDevice = action.payload
        },

        cleanAppointment: (state) => {
            state.currentTab = 'inter'
            state.startAppointment = false
            state.isAppointment = false
            state.patient = {
                name: '',
                username: '',
                patientId: 0
            }
            state.appointmentId = ''
            state.diagnosis = []
            state.treatments = []
            state.physicalExam = {} as PhysicalExamI
            state.workplan = []
            state.workplanActivities = []
            state.interrogation = ""
            state.recordId = ''
            state.updateVitals = false
            state.prognosis = []
            state.analysisEvaluation = ''
            state.isEDADehydrationRecovery = null
            state.orderId = null
            state.vSOrderId = undefined
            state.captureStudyDevice = undefined
            state.workplanOtherId = undefined
            state.reasonAppointment = undefined
            state.reasons = []
            state.patientReasons = []
            state.customerService = undefined
            state.updatedPhysicalExam = undefined
            state.temporalRelationshipCode = ''
            state.difficulty = {
                someDifficulty:null,
                difficulties:[]
            }
            state.diagnosisToUpdate = []
            state.treatmentsToUpdate = []
            state.workplanActivitiesToUpdate = []
            state.currentSuffering = ''
            state.pregestationalRiskCode = ''
            state.actualPregnancy= null
            state.previousPregnancies= []
            state.previousPregnanciesToUpdate= []
            state.pregnancyId = null
            state.womanPregnancyId = undefined
            state.appliedQuizzes = []
            state.disabilities = []
            state.disabilitiesToUpdate = []
            state.difficulties = []
            state.difficultiesRemoved = []
            state.applicationCancerCard = null
            state.telemedicineConsultation = null
            state.analysisStudyResults = ""
            state.presentsHealthCard = null
            state.completeVaccinationSchedule = null
            state.orderByDevice = null
        },
    },
})

export const { setRecordId, setTreatmentSaved, setDiagnosisSaved, setTab, deleteTreatment, deleteDiagnosis, setInterrogation, setPhysicalExam, setPhysicalExamNote, setDiagnosis, 
    setTreatment, setWorkplan, setVsOrderId, cleanAppointment, setAppointmentId, setAppointment, startAppointment, setUpdateVitals ,setPrognosis, 
    setPrognosisSaved, deletePrognosis,setAnalysisEvaluation,setIsEDADehydrationRecovery, setStudyFromDevice, removeStudyFromDevice, setWorkPlanOtherId,
    setUpdatedPhysicalExam, setUpdateReasons, setUpdatePatientReasons, setCustomerService, setUpdateTemporalRelationship, setUpdateDifficulty, setUpdateDifficulties, setDifficultiesRemoved,
    setDiagnosisToUpdate, setDiagnosisToUpdateSaved, deleteDiagnosisToUpdate, setTreatmentToUpdate, setTreatmentToUpdateSaved, deleteTreatmentToUpdate,
    setUpdateCurrentSuffering,setUpdatePregestationalRiskCode, setPregnancyId, setWomanPregnancyId,
    setRecordActualPregnancy,setRecordPreviousPregnancy, setRecordPreviousPregnancyToUpdate, setRecordPreviousPregnanciesSaved, setRecordPreviousPregnanciesToUpdateSaved,
    setWorkplanActivity, setWorkplanActivityToUpdate, setWorkplanActivitiesSaved, setWorkplanActivitiesToUpdateSaved, deleteWorkplanActivity, deleteWorkplanActivityToUpdate,
    setAppliedQuizzAdd, setAppliedQuizzesSaved, setDisability, setDisabilityToUpdate,setDisabilitiesSaved,setDisabilitiesToUpdateSaved, deleteDisability, deleteDisabilityToUpdate,
    setPresentsHealthCard, setCompleteVaccinationSchedule, setAnalysisStudyResults, setAnalysisApplicationCancerCard, setTelemedicineConsultation, setOrderByDevice
    } = newAppointmentSlice.actions
export default newAppointmentSlice.reducer
